import { z } from 'zod';
import { statusToneSchema, type StatusTone } from './common';

/**
 * How a map layer is drawn, as a document somebody can author.
 *
 * The map used to hold its cartography in code: six MapLibre layer
 * specifications with the colours written into them, keyed off a five-member
 * enum. This file is the replacement -- a closed, validated vocabulary that a
 * style editor can produce, a legend can be generated from, and a renderer can
 * compile into whatever the drawing library of the day wants.
 *
 * ## Why not just store MapLibre expressions
 *
 * Because a legend cannot be generated from one. A `match` on a property is
 * trivially readable by a person and opaque to a program: there is no way to
 * ask it which classes it defines, what they are called, or which field it
 * reads -- and those three questions are the legend, the class filter and the
 * projection in section 2.3 of docs/map-layers-design.md respectively.
 *
 * Three lesser reasons agree: expressions are a small language authored by one
 * tenant and evaluated in every viewer's browser; storing them marries the
 * database to one rendering library; and an invalid one cannot be explained to
 * whoever wrote it, because `Style.addLayer` does not throw -- it validates,
 * fires an event nobody listens to, and returns.
 *
 * ## The shape
 *
 * Three slots, one per geometry kind, because one layer can hold all three: a
 * "hazard" is a pin when somebody drops one and an area when somebody traces
 * one, and they should not need to be two layers to be drawn differently.
 *
 * Within a slot, colour and size are the two properties worth making
 * data-driven, and each is a small discriminated union. Everything else --
 * opacity, dash, outline width -- is a constant, because a data-driven dash
 * pattern is a thing nobody has ever wanted and a fourth axis of encoding
 * nobody can read.
 */

/* ------------------------------------------------------------------ colour */

/**
 * The colours a style may name.
 *
 * Tokens, never hex values. The application has a light theme and a dark one,
 * a colour is resolved against whichever is in force, and a hex picked in light
 * mode would be invisible in dark with nothing to tell the person who picked it.
 *
 * Three groups, and the grouping is the point:
 *
 * - the five status tones, which *mean* something and should be spent only on
 *   things that mean it;
 * - `primary` and `accent`, the brand's own, plus `halo` -- the surface colour,
 *   used behind a line and around a point so a feature reads against any
 *   basemap;
 * - `series1`..`series8`, which mean nothing at all and are for telling classes
 *   apart. They index the theme's `chartPalette`, which is already validated
 *   for lightness band, chroma floor and adjacent-pair separation under
 *   colour-vision deficiency -- so a categorical map inherits that work rather
 *   than repeating it badly.
 */
export const MAP_STYLE_COLORS = [
  'neutral',
  'info',
  'success',
  'warning',
  'danger',
  'primary',
  'accent',
  'halo',
  'series1',
  'series2',
  'series3',
  'series4',
  'series5',
  'series6',
  'series7',
  'series8',
] as const;
export const mapStyleColorSchema = z.enum(MAP_STYLE_COLORS);
export type MapStyleColor = (typeof MAP_STYLE_COLORS)[number];

/** The series tokens, in order, for an editor handing out distinct colours. */
export const MAP_STYLE_SERIES_COLORS = MAP_STYLE_COLORS.filter((color) =>
  color.startsWith('series'),
) as readonly MapStyleColor[];

/* ------------------------------------------------------------ field refs */

/**
 * Values a style may read, as a reference string.
 *
 * One string rather than a `{ source, name }` object, for three reasons that
 * all point the same way: it is a map key, it is a set member, and it is what
 * travels in the collection response's `fields` array. An object would have to
 * be serialised into a key at every one of those points, and two serialisations
 * that disagree is exactly the bug this format cannot have.
 *
 * - `feature:<name>` -- the feature row itself. A closed set: these are columns.
 * - `attribute:<key>` -- the feature's own bounded `attributes` map. Open, and
 *   therefore always a string on the way out; a style that wants to scale by one
 *   declares the coercion by using a numeric rule, and a value that will not
 *   parse lands in the "no value" class where the reader can see it.
 * - `subject:<kind>.<name>` -- resolved from the linked domain row by a provider
 *   registered with the field registry (design section 3).
 */
export const MAP_STYLE_FEATURE_FIELDS = ['statusTone', 'geometryKind', 'ageDays'] as const;
export type MapStyleFeatureField = (typeof MAP_STYLE_FEATURE_FIELDS)[number];

export const MAP_STYLE_FIELD_SOURCES = ['feature', 'attribute', 'subject'] as const;
export type MapStyleFieldSource = (typeof MAP_STYLE_FIELD_SOURCES)[number];

export interface ParsedStyleFieldRef {
  source: MapStyleFieldSource;
  /** Present only for `subject:` references. */
  subjectKind: string | null;
  name: string;
}

const REF_PATTERN = /^(feature|attribute|subject):([A-Za-z0-9_][A-Za-z0-9_.-]{0,63})$/;

/**
 * Parses a reference, or returns null.
 *
 * Null rather than a throw because every caller has a "this style names
 * something I cannot resolve" branch anyway -- the registry's contents change
 * as modules are added and removed, and a stored style outlives both.
 */
export function parseStyleFieldRef(ref: string): ParsedStyleFieldRef | null {
  const match = REF_PATTERN.exec(ref);
  if (!match?.[1] || !match[2]) return null;

  const source = match[1] as MapStyleFieldSource;
  const rest = match[2];

  if (source === 'subject') {
    const dot = rest.indexOf('.');
    if (dot <= 0 || dot === rest.length - 1) return null;
    const name = rest.slice(dot + 1);
    if (name.includes('.')) return null;
    return { source, subjectKind: rest.slice(0, dot), name };
  }

  if (rest.includes('.')) return null;
  if (source === 'feature' && !MAP_STYLE_FEATURE_FIELDS.includes(rest as MapStyleFeatureField)) {
    return null;
  }

  return { source, subjectKind: null, name: rest };
}

export const mapStyleFieldRefSchema = z
  .string()
  .max(80)
  .refine((value) => parseStyleFieldRef(value) !== null, {
    message: 'must be feature:<name>, attribute:<key> or subject:<kind>.<name>',
  });

export function featureFieldRef(name: MapStyleFeatureField): string {
  return `feature:${name}`;
}

export function attributeFieldRef(key: string): string {
  return `attribute:${key}`;
}

export function subjectFieldRef(kind: string, name: string): string {
  return `subject:${kind}.${name}`;
}

/**
 * A value that has crossed the wire in a style slot.
 *
 * Primitive or null, without exception. MapLibre parses a GeoJSON source on a
 * worker thread, and a nested property comes back from a hit test as a JSON
 * string rather than as the object that went in -- so code reading a nested
 * styling value would pass its unit test and produce nonsense on a real click.
 */
export const styleValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type StyleValue = z.infer<typeof styleValueSchema>;

/* ------------------------------------------------------------------ rules */

/**
 * Stops are explicit, rather than a domain plus the name of a ramp.
 *
 * The document stays total: every rendering question is answerable from the
 * stored style alone, with no table of ramp names that a future release could
 * change out from under a saved map. Ramps, quantile breaks and equal intervals
 * are the *editor's* vocabulary, and the editor emits stops.
 */
const colorStopSchema = z.object({ at: z.number(), color: mapStyleColorSchema }).strict();
const categoryStopSchema = z
  .object({ value: z.string().min(1).max(120), color: mapStyleColorSchema })
  .strict();
const sizeStopSchema = z.object({ at: z.number(), size: z.number().min(0).max(64) }).strict();

export const MAX_STYLE_STOPS = 12;

export const mapColorRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('fixed'), color: mapStyleColorSchema }).strict(),
  /**
   * What the map did before any of this existed, kept as a named option rather
   * than reconstructed out of a category rule over `feature:statusTone`.
   *
   * They would draw the same thing, and they do not mean the same thing: this
   * one says "colour by whatever this feature is linked to", which is a
   * standing instruction that keeps working when somebody links a feature to
   * something new. A category rule with five hand-written stops is a snapshot
   * of what the tones happened to be on the day it was written.
   */
  z.object({ kind: z.literal('status') }).strict(),
  z
    .object({
      kind: z.literal('category'),
      field: mapStyleFieldRefSchema,
      stops: z.array(categoryStopSchema).min(1).max(MAX_STYLE_STOPS),
      /** Everything the stops do not name, including null. Always drawn, always
       *  in the legend: a class nobody can see is where features go missing. */
      fallback: mapStyleColorSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('scale'),
      field: mapStyleFieldRefSchema,
      stops: z.array(colorStopSchema).min(2).max(MAX_STYLE_STOPS),
      fallback: mapStyleColorSchema,
    })
    .strict(),
]);
export type MapColorRule = z.infer<typeof mapColorRuleSchema>;

export const mapSizeRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('fixed'), size: z.number().min(0).max(64) }).strict(),
  z
    .object({
      kind: z.literal('scale'),
      field: mapStyleFieldRefSchema,
      stops: z.array(sizeStopSchema).min(2).max(MAX_STYLE_STOPS),
      /** The size a feature with no readable value is drawn at. Zero would hide
       *  it, which is a filter wearing a size control's clothes. */
      fallback: z.number().min(0).max(64),
    })
    .strict(),
]);
export type MapSizeRule = z.infer<typeof mapSizeRuleSchema>;

/* ------------------------------------------------------------------ slots */

const opacitySchema = z.number().min(0).max(1);

export const MAP_LINE_DASHES = ['solid', 'dashed', 'dotted'] as const;
export const mapLineDashSchema = z.enum(MAP_LINE_DASHES);
export type MapLineDash = (typeof MAP_LINE_DASHES)[number];

export const mapPointStyleSchema = z
  .object({
    color: mapColorRuleSchema,
    /** Radius in pixels at zoom 14, the zoom a project corridor is read at.
     *  Smaller zooms scale down; see the renderer's zoom ramp. */
    radius: mapSizeRuleSchema,
    opacity: opacitySchema,
    strokeColor: mapStyleColorSchema,
    strokeWidth: z.number().min(0).max(8),
  })
  .strict();
export type MapPointStyle = z.infer<typeof mapPointStyleSchema>;

export const mapLineStyleSchema = z
  .object({
    color: mapColorRuleSchema,
    width: mapSizeRuleSchema,
    opacity: opacitySchema,
    dash: mapLineDashSchema,
    /** A wider stroke in the surface colour underneath. One layer, and it is
     *  what makes a thin coloured line legible over a busy basemap. */
    casing: z.boolean(),
  })
  .strict();
export type MapLineStyle = z.infer<typeof mapLineStyleSchema>;

export const mapAreaStyleSchema = z
  .object({
    color: mapColorRuleSchema,
    /**
     * Low by default, and the editor says so when it is raised above a layer
     * holding points. An area is background information until somebody asks
     * about it; filling it solidly buries the basemap detail that says where it
     * is, and buries the pins in the layers below (design section 5).
     */
    fillOpacity: opacitySchema,
    outlineColor: mapColorRuleSchema,
    outlineWidth: z.number().min(0).max(12),
    dash: mapLineDashSchema,
  })
  .strict();
export type MapAreaStyle = z.infer<typeof mapAreaStyleSchema>;

export const mapLayerStyleSchema = z
  .object({
    point: mapPointStyleSchema,
    line: mapLineStyleSchema,
    area: mapAreaStyleSchema,
  })
  .strict();
export type MapLayerStyle = z.infer<typeof mapLayerStyleSchema>;

/* ----------------------------------------------------------- projection */

/**
 * How many distinct fields all of an organization's layer styles may name
 * between them -- one per slot the wire format carries.
 *
 * The bound exists because the collection response carries one slot per
 * distinct field on *every* feature, and `mapFeaturePropertiesSchema` is the
 * payload bound this module has always kept. Eight is generous: in practice a
 * catalog colours by one or two fields that most of its layers share.
 *
 * Checked when a style is saved rather than when a map is drawn, so the person
 * who would cause it is the person who is told -- a map that silently dropped a
 * layer's colour would be discovered by somebody else, much later.
 */
/**
 * The slot names, spelled out as literals.
 *
 * Written rather than generated, and this is the one place in the module where
 * that is the right call: these are the *keys of a wire schema*, and a schema
 * whose shape is built by `Array.from` infers as `Record<string, ...>` -- which
 * widens the properties type until the slots vanish from it entirely. The
 * runtime kept working and every consumer lost the ability to read a styling
 * value without a cast.
 *
 * Everything else derives from this tuple, so there is still one source of
 * truth: the count, the key type, and the schema shape.
 */
export const STYLE_SLOT_KEYS = ['v0', 'v1', 'v2', 'v3', 'v4', 'v5', 'v6', 'v7'] as const;

export type StyleSlotKey = (typeof STYLE_SLOT_KEYS)[number];

export const MAX_STYLE_FIELDS = STYLE_SLOT_KEYS.length;

/** The slot at an index, or `undefined` past the end -- which the projection
 *  cannot reach, because `fieldsFor` refuses a catalog that would. */
export function styleSlotKey(index: number): StyleSlotKey {
  return STYLE_SLOT_KEYS[index] as StyleSlotKey;
}

/**
 * Every field one style reads, in a stable order.
 *
 * Stable because the order decides slot assignment, and a slot assignment that
 * depended on object iteration would make two identical requests disagree about
 * which value `v0` holds.
 */
export function styleFieldRefs(style: MapLayerStyle): string[] {
  const refs: string[] = [];

  const addRule = (rule: MapColorRule | MapSizeRule): void => {
    if (rule.kind !== 'category' && rule.kind !== 'scale') return;
    if (!refs.includes(rule.field)) refs.push(rule.field);
  };

  addRule(style.point.color);
  addRule(style.point.radius);
  addRule(style.line.color);
  addRule(style.line.width);
  addRule(style.area.color);
  addRule(style.area.outlineColor);

  return refs;
}

/** The union across many styles, in the order the layers were given. */
export function unionStyleFieldRefs(styles: readonly MapLayerStyle[]): string[] {
  const refs: string[] = [];
  for (const style of styles) {
    for (const ref of styleFieldRefs(style)) {
      if (!refs.includes(ref)) refs.push(ref);
    }
  }
  return refs;
}

/* ---------------------------------------------------------------- classes */

/**
 * One row of a generated legend.
 *
 * `value` is null for the fallback class -- "everything the stops do not name",
 * which includes every feature whose styling field could not be read. It is
 * always last and always present, because a null styling field is the first
 * cause of "my map went grey" and a legend that hid the class would leave the
 * reader with a colour and no explanation.
 */
export interface MapStyleClass {
  value: string | null;
  color: MapStyleColor;
}

/**
 * The classes a colour rule defines, in legend order.
 *
 * Empty for `fixed` and `status`: neither divides the layer into anything. A
 * `status` rule *does* produce five colours, and they are deliberately not
 * classes here -- they are the product's own severity vocabulary, shown by the
 * legend as the tone key it has always had rather than as five rows repeated
 * under every layer.
 */
export function colorRuleClasses(rule: MapColorRule): MapStyleClass[] {
  if (rule.kind === 'fixed' || rule.kind === 'status') return [];

  const stops =
    rule.kind === 'category'
      ? rule.stops.map((stop) => ({ value: stop.value, color: stop.color }))
      : rule.stops.map((stop) => ({ value: String(stop.at), color: stop.color }));

  return [...stops, { value: null, color: rule.fallback }];
}

/**
 * Which class a value falls in, or null for the fallback.
 *
 * Exported and pure because the answer has to be the same in two places that
 * cannot call each other: the API counts classes for the legend, and the
 * browser compiles the same rule into a MapLibre expression. A mismatch would
 * show as a legend whose counts do not add up to what is drawn -- which is the
 * kind of discrepancy nobody reports and everybody stops trusting.
 *
 * Category matching is on the string form. A style stores `"true"` and
 * `"blocked"` and `"400"`, and a value arriving as a boolean or a number is the
 * same answer spelled by whichever layer of the stack last touched it.
 */
export function classifyValue(rule: MapColorRule, value: StyleValue | undefined): string | null {
  if (rule.kind === 'fixed' || rule.kind === 'status') return null;
  if (value === null || value === undefined) return null;

  if (rule.kind === 'category') {
    const spelled = String(value);
    return rule.stops.some((stop) => stop.value === spelled) ? spelled : null;
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return null;

  /*
   * The band a value sits in: the highest stop it reaches. Below the first stop
   * is the fallback rather than the first band -- a scale's domain is a claim
   * about the data, and a value outside it is worth seeing as an outlier rather
   * than being quietly clamped into the lowest colour.
   */
  let band: string | null = null;
  for (const stop of [...rule.stops].sort((a, b) => a.at - b.at)) {
    if (numeric >= stop.at) band = String(stop.at);
  }
  return band;
}

/**
 * The rule a layer's legend is generated from.
 *
 * A layer has three slots and the legend has one row: the one that describes
 * what is actually drawn. Picked by what the layer contains rather than by a
 * fixed preference, so a layer of traced boundaries is described by its area
 * rule and not by a point rule nothing in it uses.
 *
 * Points first when present, because a layer holding both is usually a layer of
 * things with a couple of areas drawn around them.
 */
export function primaryColorRule(
  style: MapLayerStyle,
  geometryKinds: readonly string[],
): MapColorRule {
  if (geometryKinds.includes('point')) return style.point.color;
  if (geometryKinds.includes('line')) return style.line.color;
  if (geometryKinds.includes('area')) return style.area.color;
  return style.point.color;
}

/* ------------------------------------------------------------- defaults */

/**
 * The style a new layer starts in: a neutral pin, a neutral line, a translucent
 * area, all coloured by what they are linked to.
 *
 * Coloured by `status` rather than by a fixed token, because a new layer with no
 * features in it yet is a layer whose author has not decided anything, and the
 * useful default is the one that already reflects the project's own state.
 */
export const DEFAULT_MAP_LAYER_STYLE: MapLayerStyle = {
  point: {
    color: { kind: 'status' },
    radius: { kind: 'fixed', size: 9 },
    opacity: 1,
    strokeColor: 'halo',
    strokeWidth: 1.5,
  },
  line: {
    color: { kind: 'status' },
    width: { kind: 'fixed', size: 6 },
    opacity: 1,
    dash: 'solid',
    casing: true,
  },
  area: {
    color: { kind: 'status' },
    fillOpacity: 0.18,
    outlineColor: { kind: 'status' },
    outlineWidth: 1.5,
    dash: 'solid',
  },
};

/** A layer the deployment seeds, and the translation key its name is read
 *  through until somebody renames it (design section 2.1). */
export interface BuiltinMapLayer {
  key: string;
  /** English, and only a fallback: the interface translates `builtinKey`. */
  name: string;
  /** The tone a feature in this layer takes when it is linked to nothing.
   *  A column on the row now, rather than the hard-coded table it replaced. */
  fallbackTone: StatusTone;
  style: MapLayerStyle;
}

function styled(overrides: Partial<MapLayerStyle>): MapLayerStyle {
  return { ...DEFAULT_MAP_LAYER_STYLE, ...overrides };
}

/**
 * The five layers the map had when they were an enum.
 *
 * Seeded rather than special-cased: once created they are ordinary rows that can
 * be renamed, restyled, reordered and deleted. What they keep is `builtinKey`,
 * which is only a claim about where their *name* comes from.
 */
export const BUILTIN_MAP_LAYERS: readonly BuiltinMapLayer[] = [
  {
    key: 'site',
    name: 'Sites',
    fallbackTone: 'neutral',
    style: DEFAULT_MAP_LAYER_STYLE,
  },
  {
    key: 'work-area',
    name: 'Work areas',
    fallbackTone: 'neutral',
    style: DEFAULT_MAP_LAYER_STYLE,
  },
  {
    key: 'asset',
    name: 'Assets',
    fallbackTone: 'neutral',
    style: DEFAULT_MAP_LAYER_STYLE,
  },
  {
    key: 'route',
    name: 'Routes',
    fallbackTone: 'neutral',
    style: DEFAULT_MAP_LAYER_STYLE,
  },
  {
    key: 'hazard',
    name: 'Constraints',
    /** A hazard is a hazard whether or not anything is linked to it. */
    fallbackTone: 'warning',
    style: styled({
      area: {
        ...DEFAULT_MAP_LAYER_STYLE.area,
        /** Read more often than the others, and usually the reason somebody
         *  opened the map. Slightly stronger, and dashed, so it reads as a
         *  boundary rather than as ground. */
        fillOpacity: 0.22,
        dash: 'dashed',
        outlineWidth: 2,
      },
    }),
  },
];

/** The keys, for a seed that has to create them and a test that checks it did. */
export const BUILTIN_MAP_LAYER_KEYS = BUILTIN_MAP_LAYERS.map((layer) => layer.key);

/* ----------------------------------------------------- the field catalog */

/**
 * What the style editor offers, described by whoever owns the data.
 *
 * `labelCode` and not a label: the backend does not speak the reader's language
 * (section 16). An `enum` field carries its members so the editor can lay out
 * one row per member with a colour already chosen, which is the difference
 * between a categorical style somebody configures in four clicks and one they
 * have to type the spelling of every workflow status into.
 */
export const MAP_STYLE_FIELD_TYPES = ['enum', 'number', 'tone', 'text'] as const;
export const mapStyleFieldTypeSchema = z.enum(MAP_STYLE_FIELD_TYPES);
export type MapStyleFieldType = (typeof MAP_STYLE_FIELD_TYPES)[number];

export const mapStyleFieldMemberSchema = z
  .object({
    value: z.string(),
    /** Translation key suffix, resolved against the reader's locale. */
    labelCode: z.string(),
  })
  .strict();
export type MapStyleFieldMember = z.infer<typeof mapStyleFieldMemberSchema>;

export const mapStyleFieldDescriptorSchema = z
  .object({
    ref: mapStyleFieldRefSchema,
    labelCode: z.string(),
    /** Set for `attribute:` fields, whose names are the client's own prose and
     *  therefore are not translated. Null when `labelCode` carries the name. */
    label: z.string().nullable(),
    type: mapStyleFieldTypeSchema,
    members: z.array(mapStyleFieldMemberSchema),
    /** Observed range, for a numeric field, so the editor can propose a domain
     *  instead of asking the reader to guess one. Null when unknown or empty. */
    range: z.tuple([z.number(), z.number()]).nullable(),
    /** How many of the project's features can answer this field at all. A field
     *  nothing has is a field the editor sorts to the bottom and marks. */
    coverage: z.number().int().nonnegative(),
  })
  .strict();
export type MapStyleFieldDescriptor = z.infer<typeof mapStyleFieldDescriptorSchema>;

export const mapStyleFieldCatalogSchema = z
  .object({
    projectId: z.string().uuid(),
    fields: z.array(mapStyleFieldDescriptorSchema),
  })
  .strict();
export type MapStyleFieldCatalog = z.infer<typeof mapStyleFieldCatalogSchema>;

/** The tones, as members, for the `feature:statusTone` descriptor and for any
 *  provider whose field is a tone. One list, so they cannot disagree. */
export const STATUS_TONE_MEMBERS: readonly MapStyleFieldMember[] = statusToneSchema.options.map(
  (tone) => ({ value: tone, labelCode: `tone.${tone}` }),
);
